import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, PlusIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import Header from '../Header';
import { cityService } from '../../services/cityService';
import FreezoneForm from './FreezoneForm';

const CityDetail = () => {
  const { cityId: cityIdParam } = useParams();
  const navigate = useNavigate();
  
  // All hooks must be called before any early returns
  const [city, setCity] = useState(null);
  const [freezones, setFreezones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFreezoneForm, setShowFreezoneForm] = useState(false);
  
  // Use cityId as string for MongoDB ObjectId
  const cityId = cityIdParam;
  
  // Validate cityId - check if it's a valid ObjectId format (24 hex characters)
  const isValidCityId = cityId && cityId.length === 24 && /^[0-9a-fA-F]{24}$/.test(cityId);

  useEffect(() => {
    if (isValidCityId) {
      fetchCityAndFreezones();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCityAndFreezones is stable
  }, [cityId, isValidCityId]);

  const fetchCityAndFreezones = async () => {
    try {
      setLoading(true);
      
      // Fetch city details (which includes freezones)
      const cityResponse = await cityService.getCityById(cityId);
      const cityData = cityResponse.city || cityResponse;
      setCity(cityData);
      
      // Extract freezones from city response (no need for separate API call)
      const freezonesData = cityData.freezones || [];
      setFreezones(Array.isArray(freezonesData) ? freezonesData : []);
      
    } catch (error) {
      console.error('Error fetching city and freezones:', error);
      toast.error('Failed to load city details');
    } finally {
      setLoading(false);
    }
  };

  const handleFreezoneCreated = () => {
    setShowFreezoneForm(false);
    fetchCityAndFreezones();
    toast.success('Freezone created successfully');
  };

  if (!isValidCityId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid City ID</h2>
            <p className="mt-2 text-gray-600">The city ID provided is not valid.</p>
            <Link
              to="/cities"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to Cities
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">City not found</h2>
            <p className="mt-2 text-gray-600">The city you're looking for doesn't exist.</p>
            <Link
              to="/cities"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to Cities
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/cities"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Cities
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-2 text-gray-400">/</span>
                <span className="text-sm font-medium text-gray-500">{city.name}</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* City Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="relative h-64">
            {city.image_url && (
              <img
                src={city.image_url}
                alt={city.name}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h1 className="text-3xl font-bold text-white mb-2">{city.name}</h1>
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  city.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {city.status}
                </span>
                <span className="text-white/80 text-sm">
                  {freezones.length} Freezone{freezones.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
          
          {/* City Benefits */}
          {city.benefits && city.benefits.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">City Benefits</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {city.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <p className="text-sm text-gray-600">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Freezones Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Freezones in {city.name}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage freezones and their pricing packages
                </p>
              </div>
              <button
                onClick={() => setShowFreezoneForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Freezone
              </button>
            </div>
          </div>

          <div className="p-6">
            {freezones.length === 0 ? (
              <div className="text-center py-12">
                <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No freezones</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a new freezone for {city.name}.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowFreezoneForm(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Freezone
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {freezones.map((freezone) => (
                  <div
                    key={freezone.id}
                    className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 overflow-hidden transform hover:-translate-y-1"
                    onClick={() => navigate(`/cities/${cityId}/freezones/${freezone.id}`)}
                  >
                    {/* Header with gradient background */}
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            <BuildingOfficeIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold">{freezone.name}</h3>
                            <p className="text-blue-100 text-sm">Freezone</p>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          freezone.status === 'active' 
                            ? 'bg-green-400/20 text-green-100 border border-green-300/30' 
                            : 'bg-red-400/20 text-red-100 border border-red-300/30'
                        }`}>
                          {freezone.status === 'active' ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5 space-y-4">
                      {/* Timeline */}
                      {freezone.timeline && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium">Processing Time:</span>
                          <span className="text-sm">{freezone.timeline}</span>
                        </div>
                      )}

                      {/* Special Offer */}
                      {freezone.special_offer && (
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-yellow-600 text-lg">🎉</span>
                            <p className="text-sm font-semibold text-yellow-800">{freezone.special_offer}</p>
                          </div>
                        </div>
                      )}

                      {/* Benefits Preview */}
                      {freezone.benefits && freezone.benefits.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                            Key Benefits
                          </h4>
                          <div className="space-y-2">
                            {freezone.benefits.slice(0, 2).map((benefit, index) => (
                              <div key={index} className="flex items-start space-x-2">
                                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                  {benefit.replace(/\*\*(.*?)\*\*/g, '$1')}
                                </p>
                              </div>
                            ))}
                            {freezone.benefits.length > 2 && (
                              <div className="pt-2 border-t border-gray-100">
                                <p className="text-xs text-blue-600 font-medium">
                                  +{freezone.benefits.length - 2} more benefits available
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Footer */}
                      <div className="pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-medium">View Pricing & Details</span>
                          <div className="flex items-center space-x-1 text-blue-600 group-hover:text-blue-700 transition-colors">
                            <span className="text-sm font-medium">Explore</span>
                            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showFreezoneForm && (
        <FreezoneForm
          onClose={() => setShowFreezoneForm(false)}
          onSuccess={handleFreezoneCreated}
        />
      )}
    </div>
  );
};

export default CityDetail; 
